// components/SearchFilterPanel.jsx
import React, { useEffect, useCallback, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
    Box, Button, Checkbox, Collapse, FormControl, Grid, InputLabel,
    ListSubheader, MenuItem, OutlinedInput, Select, Typography,
    Autocomplete, TextField, Chip, ListItemText, Tooltip, IconButton,
    Paper, Divider, FormControlLabel, useMediaQuery, useTheme, InputAdornment
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import FilterListIcon from '@mui/icons-material/FilterList';
import SearchIcon from '@mui/icons-material/Search';
import { CONSTANTS } from '../constants';
import { NORMALIZED_INGREDIENT_OPTIONS } from '../constants/normalizedIngredients';
import { useEnums } from '../hooks/useEnums';
import { MultiSelect } from './MultiSelect';
import { NumberRangeFilter } from './NumberRangeFilter';

// Helper functions
const renderSelectedItems = (selected, itemMap, defaultText = "None selected") => {
    if (!Array.isArray(selected) || selected.length === 0) {
        return defaultText;
    }
    // Convert the array to a single string before returning
    const selectedText = selected.map((key) => itemMap[key]).reduce((acc, curr) => acc + ', ' + curr);
    return selectedText;
};

const renderSelectedProteins = (selected) => {
    if (!Array.isArray(selected) || selected.length === 0) {
        return "Any protein";
    }
    const validSelections = selected.filter(item => item && item.trim() !== '');
    // Convert the array to a single string before returning
    return validSelections.reduce((acc, curr) => acc + ', ' + curr);
};

const whiteCheckboxSx = {
    p: 0.5,
    mr: 0.75,
    '& .MuiSvgIcon-root': {
        backgroundColor: '#fff',
        borderRadius: '2px'
    },
    '&.Mui-checked .MuiSvgIcon-root': {
        backgroundColor: '#fff'
    }
};

const booleanCheckboxLabelSx = {
    m: 0,
    ml: 0,
    mr: 0,
    minHeight: '34px',
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    columnGap: 0,
    '& .MuiCheckbox-root': {
        flexShrink: 0
    },
    '& .MuiFormControlLabel-label': {
        display: 'block',
        flex: 1,
        width: 'auto'
    }
};

const booleanCheckboxWrapperSx = {
    width: '100%',
    minWidth: '100%',
    minHeight: '34px',
    display: 'flex',
    alignItems: 'center',
    boxSizing: 'border-box',
    px: 0,
    '@media (max-width: 768px)': {
        width: '100%',
        minWidth: '100%',
        px: 0
    }
};

const SearchFilterPanel = ({ filters, setFilter, open, setOpen, resetFilters }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const enums = useEnums();
    const [searchParams, setSearchParams] = useSearchParams();
    const updateTimeoutRef = useRef(null);

    // Load filters from URL when component mounts
    useEffect(() => {
        // Read filters from URL parameters
        const urlFilters = {};
        
        // Handle text filters
        if (searchParams.has('searchText')) {
            setFilter('text', 'searchText', searchParams.get('searchText'));
        }
        
        // Handle multiselect filters
        ['brands', 'categories', 'proteins', 'excludedProteins', 'lifestages', 'textures', 'healthBenefits', 'formats', 'excludedIngredients', 'includedIngredients'].forEach(key => {
            if (searchParams.has(key)) {
                try {
                    const value = JSON.parse(searchParams.get(key));
                    if (Array.isArray(value)) {
                        setFilter('multiSelect', key, value);
                    }
                } catch (e) {
                    // If parsing fails, ignore this parameter
                    console.error(`Error parsing ${key} from URL`, e);
                }
            }
        });
        
        // Handle boolean filters
        ['grainFree', 'rawFood', 'gmoFree', 'noveltyProteins', 'singleProtein', 'hydrolyzedProtein', 'includeGuaranteedAnalysis'].forEach(key => {
            if (searchParams.has(key)) {
                try {
                    const value = JSON.parse(searchParams.get(key));
                    if (Array.isArray(value)) {
                        setFilter('boolean', key, value);
                    }
                } catch (e) {
                    console.error(`Error parsing ${key} from URL`, e);
                }
            }
        });
        
        // Handle range filters
        ['calories', 'protein', 'phosphorus', 'sodium', 'carbs', 'calcium', 'fat'].forEach(key => {
            const minKey = `${key}Min`;
            const maxKey = `${key}Max`;
            
            if (searchParams.has(minKey)) {
                setFilter('range', key, { min: searchParams.get(minKey) });
            }
            
            if (searchParams.has(maxKey)) {
                setFilter('range', key, { max: searchParams.get(maxKey) });
            }
        });
    }, []);

    // Update URL parameters when filters change (very conservative approach)
    useEffect(() => {
        // Clear any pending timeout
        if (updateTimeoutRef.current) {
            clearTimeout(updateTimeoutRef.current);
        }
        
        // Only update URL parameters after 3 seconds of no filter changes
        // This ensures navigation won't be interfered with
        updateTimeoutRef.current = setTimeout(() => {
            const newParams = {};
            
            // Add text filters
            if (filters.text.searchText) {
                newParams.searchText = filters.text.searchText;
            }
            
            // Add multiselect filters
            Object.entries(filters.multiSelect).forEach(([key, value]) => {
                if (Array.isArray(value) && value.length > 0) {
                    newParams[key] = JSON.stringify(value);
                }
            });
            
            // Add boolean filters
            Object.entries(filters.boolean).forEach(([key, value]) => {
                if (Array.isArray(value) && value.length > 0) {
                    newParams[key] = JSON.stringify(value);
                }
            });
            
            // Add range filters
            Object.entries(filters.range).forEach(([key, value]) => {
                if (value.min !== undefined && value.min !== '') {
                    newParams[`${key}Min`] = value.min;
                }
                if (value.max !== undefined && value.max !== '') {
                    newParams[`${key}Max`] = value.max;
                }
            });
            
            // Update URL parameters
            setSearchParams(newParams, { replace: true });
        }, 3000); // 3 second delay
    }, [filters, setSearchParams]);

    // Clean up timeout on unmount
    useEffect(() => {
        return () => {
            if (updateTimeoutRef.current) {
                clearTimeout(updateTimeoutRef.current);
            }
        };
    }, []);

    // Wrap the reset function to also clear URL params
    const handleResetFilters = useCallback(() => {
        resetFilters();
        setSearchParams({}, { replace: true });
    }, [resetFilters, setSearchParams]);

    // Check if any filters are active
    const hasActiveFilters = () => {
        // Check text filters
        if (filters.text.searchText && filters.text.searchText.trim() !== '') {
            return true;
        }
        
        // Check multiselect filters
        for (const key in filters.multiSelect) {
            if (Array.isArray(filters.multiSelect[key]) && filters.multiSelect[key].length > 0) {
                return true;
            }
        }
        
        // Check boolean filters
        for (const key in filters.boolean) {
            if (Array.isArray(filters.boolean[key]) && filters.boolean[key].length > 0) {
                return true;
            }
        }
        
        // Check range filters
        for (const key in filters.range) {
            if ((filters.range[key].min !== undefined && filters.range[key].min !== '') ||
                (filters.range[key].max !== undefined && filters.range[key].max !== '')) {
                return true;
            }
        }
        
        return false;
    };

    // Check if a protein type is selected (all subtypes are checked)
    const isTypeSelected = (type) => {
        const typeProteins = enums.proteinTypes[type];
        return typeProteins.every(protein => filters.multiSelect.proteins.includes(protein));
    };

    // Check if a protein type is indeterminate (some but not all subtypes are checked)
    const isTypeIndeterminate = (type) => {
        const typeProteins = enums.proteinTypes[type];
        const selectedCount = typeProteins.filter(protein =>
            filters.multiSelect.proteins.includes(protein)
        ).length;

        return selectedCount > 0 && selectedCount < typeProteins.length;
    };

    // Handle protein type selection (parent checkbox)
    const handleTypeChange = (event) => {
        const selectedType = event.target.value;
        const typesProteins = enums.proteinTypes[selectedType];

        // Check if all proteins of this type are currently selected
        const allTypeProteinsSelected = typesProteins.every(protein =>
            filters.multiSelect.proteins.includes(protein)
        );

        if (allTypeProteinsSelected) {
            // If all proteins of this type are selected, unselect them all
            setFilter('multiSelect', 'proteins',
                filters.multiSelect.proteins.filter(protein => !typesProteins.includes(protein))
            );
        } else {
            // If not all proteins of this type are selected, select all proteins of this type
            // while keeping other selected proteins
            const currentProteins = new Set(filters.multiSelect.proteins);
            typesProteins.forEach(protein => currentProteins.add(protein));
            setFilter('multiSelect', 'proteins', Array.from(currentProteins));
        }
    };

    // Handle individual protein selection
    const handleSubtypeChange = (event) => {
        const protein = event.target.value;

        // If it's a string (single protein), toggle it
        if (typeof protein === 'string') {
            if (filters.multiSelect.proteins.includes(protein)) {
                setFilter('multiSelect', 'proteins',
                    filters.multiSelect.proteins.filter(p => p !== protein)
                );
            } else {
                setFilter('multiSelect', 'proteins', [...filters.multiSelect.proteins, protein]);
            }
        }
        // If it's an array (from the Select), use it directly
        else if (Array.isArray(protein)) {
            setFilter('multiSelect', 'proteins', protein);
        }
    };

    // Check if a texture group is fully selected
    const isTextureGroupSelected = (group) => {
        const groupTextures = enums.textureGroups[group];
        return groupTextures.every(texture => filters.multiSelect.textures.includes(texture));
    };

    // Check if a texture group is indeterminate
    const isTextureGroupIndeterminate = (group) => {
        const groupTextures = enums.textureGroups[group];
        const selectedCount = groupTextures.filter(texture =>
            filters.multiSelect.textures.includes(texture)
        ).length;
        return selectedCount > 0 && selectedCount < groupTextures.length;
    };

    // Handle texture group selection (parent checkbox)
    const handleTextureGroupChange = (event) => {
        const selectedGroup = event.target.value;
        const groupTextures = enums.textureGroups[selectedGroup];
        const allSelected = groupTextures.every(texture =>
            filters.multiSelect.textures.includes(texture)
        );
        if (allSelected) {
            setFilter('multiSelect', 'textures',
                filters.multiSelect.textures.filter(texture => !groupTextures.includes(texture))
            );
        } else {
            const current = new Set(filters.multiSelect.textures);
            groupTextures.forEach(texture => current.add(texture));
            setFilter('multiSelect', 'textures', Array.from(current));
        }
    };

    // Handle individual texture selection
    const handleTextureChange = (event) => {
        const texture = event.target.value;
        if (typeof texture === 'string') {
            if (filters.multiSelect.textures.includes(texture)) {
                setFilter('multiSelect', 'textures',
                    filters.multiSelect.textures.filter(t => t !== texture)
                );
            } else {
                setFilter('multiSelect', 'textures', [...filters.multiSelect.textures, texture]);
            }
        } else if (Array.isArray(texture)) {
            setFilter('multiSelect', 'textures', texture);
        }
    };

    const renderSelectedTextures = (selected) => {
        if (!Array.isArray(selected) || selected.length === 0) {
            return "Any texture";
        }
        return selected.filter(item => item && item.trim() !== '').join(', ');
    };

    // Check if any detailed filters are active
    const hasActiveDetailedFilters = () => {
        // Check range filters (detailed filters)
        for (const key in filters.range) {
            if ((filters.range[key].min !== undefined && filters.range[key].min !== '') ||
                (filters.range[key].max !== undefined && filters.range[key].max !== '')) {
                return true;
            }
        }
        
        // Check boolean filters (detailed filters)
        for (const key in filters.boolean) {
            if (Array.isArray(filters.boolean[key]) && filters.boolean[key].length > 0) {
                return true;
            }
        }
        
        return false;
    };

    // Count active detailed filters
    const getActiveDetailedFiltersCount = () => {
        let count = 0;
        
        // Count range filters
        Object.entries(filters.range).forEach(([key, value]) => {
            if ((value.min !== undefined && value.min !== '') ||
                (value.max !== undefined && value.max !== '')) {
                count++;
            }
        });
        
        // Count boolean filters
        Object.entries(filters.boolean).forEach(([key, value]) => {
            if (Array.isArray(value) && value.length > 0) {
                count++;
            }
        });
        
        return count;
    };

    return (
        <Box className="search-filter-container" sx={{ width: '100%', overflow: 'hidden' }}>
            <Grid container spacing={isMobile ? 0 : 1.5} sx={isMobile ? {
                flexDirection: 'column',
                gap: '8px',
                width: '100%',
                m: 0,
                '& > .MuiGrid-item': {
                    p: 0,
                    maxWidth: '100%',
                    flexBasis: '100%',
                    width: '100%',
                    minWidth: 0,
                },
            } : {}}>
                {/* Name Filter */}
                <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="subtitle2" sx={{ fontSize: '0.85rem' }}>Product Name</Typography>
                    <FormControl size="small" className="filter-select-container" fullWidth>
                        <TextField
                            type="text"
                            value={filters.text.searchText}
                            onChange={(e) => setFilter('text', 'searchText', e.target.value.toLowerCase())}
                            placeholder="Search by product name"
                            size="small"
                            fullWidth
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ fontSize: '1.1rem', color: 'action.active' }} />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </FormControl>
                </Grid>

                {/* Brand Filter */}
                <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="subtitle2" sx={{ fontSize: '0.85rem' }}>Brand</Typography>
                    <FormControl size="small" className="filter-select-container" fullWidth>
                        <Autocomplete
                            multiple
                            fullWidth
                            options={Object.keys(enums.brands).sort((a, b) => enums.brands[a].localeCompare(enums.brands[b]))}
                            getOptionLabel={(option) => enums.brands[option]}
                            value={filters.multiSelect.brands}
                            onChange={(event, newValue) => setFilter('multiSelect', 'brands', newValue)}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    placeholder="Any brand"
                                    size="small"
                                    className="filter-select"
                                />
                            )}
                            renderTags={(value, getTagProps) =>
                                value.map((option, index) => (
                                    <Chip
                                        label={enums.brands[option]}
                                        size="small"
                                        {...getTagProps({ index })}
                                    />
                                ))
                            }
                            renderOption={(props, option, { selected }) => (
                                <MenuItem {...props} className="filter-menu-item">
                                    <Checkbox checked={selected} className="filter-checkbox" />
                                    <ListItemText primary={enums.brands[option]} />
                                </MenuItem>
                            )}
                            size="small"
                            disablePortal
                            disableCloseOnSelect
                            noOptionsText=""
                            ListboxProps={{
                                style: { maxHeight: '300px' }
                            }}
                            PopperProps={{
                                style: { width: 'fit-content' }
                            }}
                        />
                    </FormControl>
                </Grid>

                {/* Category Filter */}
                <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="subtitle2" sx={{ fontSize: '0.85rem' }}>Category</Typography>
                    <MultiSelect
                        options={filters.multiSelect.categories}
                        value={filters.multiSelect.categories}
                        onChange={(value) => setFilter('multiSelect', 'categories', value)}
                        renderValue={() => renderSelectedItems(filters.multiSelect.categories, enums.categories, "Any category")}
                        itemMap={enums.categories}
                    />
                </Grid>

                {/* Protein Filter */}
                <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="subtitle2" sx={{ fontSize: '0.85rem' }}>Proteins</Typography>
                    <FormControl size="small" className="filter-select-container" fullWidth>
                        <Select
                            multiple
                            value={filters.multiSelect.proteins}
                            onChange={handleSubtypeChange}
                            renderValue={renderSelectedProteins}
                            MenuProps={{
                                PaperProps: {
                                    style: { maxHeight: 400, minWidth: 300 },
                                },
                                disableScrollLock: true, // Prevents layout shift caused by scroll lock
                            }}
                            className="filter-select"
                            displayEmpty
                        >
                            {Object.entries(enums.proteinTypes).flatMap(([type, subtypes]) => [
                                <ListSubheader key={`header-${type}`} className="filter-subheader">
                                    <Checkbox
                                        checked={isTypeSelected(type)}
                                        indeterminate={isTypeIndeterminate(type)}
                                        onChange={(e) => handleTypeChange(e)}
                                        value={type}
                                        onClick={(e) => e.stopPropagation()} // Important to prevent dropdown closing
                                    />
                                    {type}
                                </ListSubheader>,
                                ...subtypes.map((subtype) => (
                                    <MenuItem
                                        key={subtype}
                                        value={subtype}
                                        className="filter-menu-subitem"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleSubtypeChange({ target: { value: subtype } });
                                        }}
                                    >
                                        <Checkbox
                                            checked={filters.multiSelect.proteins.includes(subtype)}
                                            className="filter-checkbox"
                                        />
                                        {subtype}
                                    </MenuItem>
                                ))
                            ])}
                        </Select>
                    </FormControl>
                </Grid>

                {/* Lifestage Filter */}
                <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="subtitle2" sx={{ fontSize: '0.85rem' }}>Lifestage</Typography>
                    <MultiSelect
                        label="Lifestage"
                        options={filters.multiSelect.lifestages}
                        value={filters.multiSelect.lifestages}
                        onChange={(value) => setFilter('multiSelect', 'lifestages', value)}
                        renderValue={() => renderSelectedItems(filters.multiSelect.lifestages, enums.lifestages, "Any lifestage")}
                        itemMap={enums.lifestages}
                    />
                </Grid>

                {/* Format Filter */}
                <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="subtitle2" sx={{ fontSize: '0.85rem' }}>Format</Typography>
                    <MultiSelect
                        label="Format"
                        options={filters.multiSelect.formats}
                        value={filters.multiSelect.formats}
                        onChange={(value) => setFilter('multiSelect', 'formats', value)}
                        renderValue={() => renderSelectedItems(filters.multiSelect.formats, enums.formats, "Any format")}
                        itemMap={enums.formats}
                        sortAlphabetically
                    />
                </Grid>

                {/* Texture Filter */}
                <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="subtitle2" sx={{ fontSize: '0.85rem' }}>Texture</Typography>
                    <FormControl size="small" className="filter-select-container" fullWidth>
                        <Select
                            multiple
                            value={filters.multiSelect.textures}
                            onChange={handleTextureChange}
                            renderValue={renderSelectedTextures}
                            MenuProps={{
                                PaperProps: {
                                    style: { maxHeight: 400, minWidth: 300 },
                                },
                                disableScrollLock: true,
                            }}
                            className="filter-select"
                            displayEmpty
                        >
                            {Object.entries(enums.textureGroups).flatMap(([group, textures]) => [
                                <ListSubheader key={`header-${group}`} className="filter-subheader">
                                    <Checkbox
                                        checked={isTextureGroupSelected(group)}
                                        indeterminate={isTextureGroupIndeterminate(group)}
                                        onChange={(e) => handleTextureGroupChange(e)}
                                        value={group}
                                        onClick={(e) => e.stopPropagation()}
                                    />
                                    {group}
                                </ListSubheader>,
                                ...textures.map((texture) => (
                                    <MenuItem
                                        key={texture}
                                        value={texture}
                                        className="filter-menu-subitem"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleTextureChange({ target: { value: texture } });
                                        }}
                                    >
                                        <Checkbox
                                            checked={filters.multiSelect.textures.includes(texture)}
                                            className="filter-checkbox"
                                        />
                                        {texture}
                                    </MenuItem>
                                ))
                            ])}
                        </Select>
                    </FormControl>
                </Grid>

                {/* Health Benefits Filter */}
                <Grid item xs={12} sm={6} md={4}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontSize: '0.85rem' }}>Health Benefits</Typography>
                    </Box>
                    <MultiSelect
                        label="Health Benefits"
                        options={filters.multiSelect.healthBenefits}
                        value={filters.multiSelect.healthBenefits}
                        onChange={(value) => setFilter('multiSelect', 'healthBenefits', value)}
                        renderValue={() => renderSelectedItems(filters.multiSelect.healthBenefits, enums.healthBenefits, "Any health benefit")}
                        itemMap={enums.healthBenefits}
                        sortAlphabetically
                    />
                </Grid>

                {/* Ingredient Exclusion Filter */}
                <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="subtitle2" sx={{ fontSize: '0.85rem' }}>Ingredients to avoid</Typography>
                    <FormControl size="small" className="filter-select-container" fullWidth>
                        <Autocomplete
                            multiple
                            fullWidth
                            options={NORMALIZED_INGREDIENT_OPTIONS}
                            getOptionLabel={(option) => option.name}
                            value={filters.multiSelect.excludedIngredients}
                            onChange={(event, newValue) => setFilter('multiSelect', 'excludedIngredients', newValue)}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    placeholder="Exclude ingredients..."
                                    size="small"
                                    className="filter-select"
                                />
                            )}
                            renderTags={(value, getTagProps) =>
                                value.map((option, index) => (
                                    <Chip
                                        label={option.name}
                                        size="small"
                                        {...getTagProps({ index })}
                                    />
                                ))
                            }
                            renderOption={(props, option, { selected }) => (
                                <MenuItem {...props} className="filter-menu-item">
                                    <Checkbox checked={selected} className="filter-checkbox" />
                                    <ListItemText primary={option.name} />
                                </MenuItem>
                            )}
                            size="small"
                            disablePortal
                            disableCloseOnSelect
                            noOptionsText="No ingredients found"
                            ListboxProps={{
                                style: { maxHeight: '300px' }
                            }}
                            PopperProps={{
                                style: { width: 'fit-content' }
                            }}
                            filterOptions={(options, { inputValue }) => {
                                const filtered = options.filter(option =>
                                    option.name.toLowerCase().includes(inputValue.toLowerCase())
                                );
                                return filtered;
                            }}
                        />
                    </FormControl>
                </Grid>

                

                 {/* Enhanced Detailed Filters Section */}
                 {isMobile ? (
                     // Mobile: Show advanced filters directly with a label
                     <Grid item xs={12} sx={{ 
                         width: '100%',
                         mt: 2,
                         mb: 1
                     }}>
                         <Divider sx={{ mb: 2 }} />
                         <Typography 
                             variant="h6" 
                             sx={{ 
                                 fontSize: '1.1rem',
                                 fontWeight: 600,
                                 color: '#333',
                                 mb: 0,
                                 mt: 1
                             }}
                         >
                             Advanced Filters
                         </Typography>
                        <Box className="detailed-filters-container" sx={{
                            width: '100%', mt: 1, pt: 1,
                            '& .MuiGrid-container': {
                                flexDirection: 'column',
                                gap: '8px',
                                m: 0,
                                width: '100%',
                            },
                            '& .MuiGrid-container + .MuiGrid-container': {
                                mt: '10px',
                            },
                            '& .MuiGrid-container > .MuiGrid-item': {
                                p: 0,
                                maxWidth: '100%',
                                flexBasis: '100%',
                                width: '100%',
                                minWidth: 0,
                            },
                            '& .single-number-input, & .multiple-number-input, & input[type="number"]': {
                                width: '100% !important',
                                boxSizing: 'border-box',
                                height: '36px',
                            },
                        }}>
                                {/* Row 1: Nutrient Filters */}
                                <Grid container spacing={2} sx={{ width: '100%', mb: 2 }}>
                                    {/* Protein % Filter */}
                                    <Grid item xs={12} sm={6} md={3}>
                                        <NumberRangeFilter
                                            label="Protein (%)"
                                            minValue={filters.range.protein.min}
                                            maxValue={filters.range.protein.max}
                                            onMinChange={(value) => setFilter('range', 'protein', { min: value })}
                                            onMaxChange={(value) => setFilter('range', 'protein', { max: value })}
                                        />
                                    </Grid>

                                    {/* Phosphorus Filter */}
                                    <Grid item xs={3} md={2}>
                                        <Typography variant="subtitle2" sx={{ fontSize: '0.85rem' }}>Phosphorus (%)</Typography>
                                        <input
                                            type="number"
                                            value={filters.range.phosphorus.max}
                                            onChange={(e) => setFilter('range', 'phosphorus', { max: e.target.value })}
                                            placeholder="Max"
                                            className="single-number-input"
                                        />
                                    </Grid>

                                    {/* Sodium Filter */}
                                    <Grid item xs={3} md={2}>
                                        <Typography variant="subtitle2" sx={{ fontSize: '0.85rem' }}>Sodium (%)</Typography>
                                        <input
                                            type="number"
                                            value={filters.range.sodium.max}
                                            onChange={(e) => setFilter('range', 'sodium', { max: e.target.value })}
                                            placeholder="Max"
                                            className="single-number-input"
                                        />
                                    </Grid>

                                    {/* Carbs Filter */}
                                    <Grid item xs={12} sm={6} md={3}>
                                        <NumberRangeFilter
                                            label="Carbs (%)"
                                            minValue={filters.range.carbs.min}
                                            maxValue={filters.range.carbs.max}
                                            onMinChange={(value) => setFilter('range', 'carbs', { min: value })}
                                            onMaxChange={(value) => setFilter('range', 'carbs', { max: value })}
                                        />
                                    </Grid>

                                    {/* Calcium Filter */}
                                    <Grid item xs={3} md={2}>
                                        <Typography variant="subtitle2" sx={{ fontSize: '0.85rem' }}>Calcium (%)</Typography>
                                        <input
                                            type="number"
                                            value={filters.range.calcium.max}
                                            onChange={(e) => setFilter('range', 'calcium', { max: e.target.value })}
                                            placeholder="Max"
                                            className="single-number-input"
                                        />
                                    </Grid>

                                    {/* Fat Filter */}
                                    <Grid item xs={3} md={2}>
                                        <Typography variant="subtitle2" sx={{ fontSize: '0.85rem' }}>Fat (%)</Typography>
                                        <input
                                            type="number"
                                            value={filters.range.fat.max}
                                            onChange={(e) => setFilter('range', 'fat', { max: e.target.value })}
                                            placeholder="Max"
                                            className="single-number-input"
                                        />
                                    </Grid>

                                    <Grid item xs={3} md={2}>
                                        <Typography variant="subtitle2" sx={{ fontSize: '0.85rem' }}>Guaranteed Analysis (Less accurate)</Typography>
                                        <Box sx={booleanCheckboxWrapperSx} className="filter-select-container">
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={(filters.boolean.includeGuaranteedAnalysis || []).includes('TRUE')}
                                                        onChange={(event) =>
                                                            setFilter(
                                                                'boolean',
                                                                'includeGuaranteedAnalysis',
                                                                event.target.checked ? ['TRUE'] : []
                                                            )
                                                        }
                                                        size="small"
                                                        sx={whiteCheckboxSx}
                                                    />
                                                }
                                                label="Include Guaranteed Analysis"
                                                sx={booleanCheckboxLabelSx}
                                            />
                                        </Box>
                                    </Grid>
                                </Grid>

                                {/* Calories Filter Section */}
                                <Grid container spacing={2} sx={{ width: '100%', mb: 2 }}>
                                    <Grid item xs={12}>
                                        <NumberRangeFilter
                                            label="Calories (kcal per kg)"
                                            minValue={filters.range.calories.min}
                                            maxValue={filters.range.calories.max}
                                            onMinChange={(value) => setFilter('range', 'calories', { min: value })}
                                            onMaxChange={(value) => setFilter('range', 'calories', { max: value })}
                                        />
                                    </Grid>
                                </Grid>

                                {/* Row 2: Proteins to Exclude and Ingredients to Include Filters */}
                                <Grid container spacing={2} sx={{ width: '100%', mb: 2 }}>
                                    <Grid item xs={12} sm={6} md={2}>
                                        <Typography variant="subtitle2" sx={{ fontSize: '0.85rem' }}>Proteins to Exclude</Typography>
                                        <FormControl size="small" className="filter-select-container" fullWidth>
                                            <Select
                                                multiple
                                                value={filters.multiSelect.excludedProteins}
                                                onChange={(event) => {
                                                    const protein = event.target.value;
                                                    if (typeof protein === 'string') {
                                                        if (filters.multiSelect.excludedProteins.includes(protein)) {
                                                            setFilter('multiSelect', 'excludedProteins',
                                                                filters.multiSelect.excludedProteins.filter(p => p !== protein)
                                                            );
                                                        } else {
                                                            setFilter('multiSelect', 'excludedProteins', [...filters.multiSelect.excludedProteins, protein]);
                                                        }
                                                    } else if (Array.isArray(protein)) {
                                                        setFilter('multiSelect', 'excludedProteins', protein);
                                                    }
                                                }}
                                                renderValue={(selected) => {
                                                    if (!Array.isArray(selected) || selected.length === 0) {
                                                        return "None selected";
                                                    }
                                                    const validSelections = selected.filter(item => item && item.trim() !== '');
                                                    if (validSelections.length === 0) {
                                                        return "None selected";
                                                    }
                                                    return validSelections.reduce((acc, curr) => acc + ', ' + curr);
                                                }}
                                                MenuProps={{
                                                    PaperProps: {
                                                        style: { maxHeight: 400, minWidth: 300 },
                                                    },
                                                    disableScrollLock: true,
                                                }}
                                                className="filter-select"
                                                displayEmpty
                                                sx={{
                                                    backgroundColor: 'white'
                                                }}
                                            >
                                                {Object.entries(enums.proteinTypes).flatMap(([type, subtypes]) => [
                                                    <ListSubheader key={`header-excluded-${type}`} className="filter-subheader">
                                                        <Checkbox
                                                            checked={subtypes.every(subtype => filters.multiSelect.excludedProteins.includes(subtype))}
                                                            indeterminate={
                                                                subtypes.some(subtype => filters.multiSelect.excludedProteins.includes(subtype)) &&
                                                                !subtypes.every(subtype => filters.multiSelect.excludedProteins.includes(subtype))
                                                            }
                                                            onChange={(e) => {
                                                                const selectedType = type;
                                                                const typesProteins = enums.proteinTypes[selectedType];
                                                                const allTypeProteinsSelected = typesProteins.every(protein =>
                                                                    filters.multiSelect.excludedProteins.includes(protein)
                                                                );
                                                                if (allTypeProteinsSelected) {
                                                                    setFilter('multiSelect', 'excludedProteins',
                                                                        filters.multiSelect.excludedProteins.filter(protein => !typesProteins.includes(protein))
                                                                    );
                                                                } else {
                                                                    const currentProteins = new Set(filters.multiSelect.excludedProteins);
                                                                    typesProteins.forEach(protein => currentProteins.add(protein));
                                                                    setFilter('multiSelect', 'excludedProteins', Array.from(currentProteins));
                                                                }
                                                            }}
                                                            value={type}
                                                            onClick={(e) => e.stopPropagation()}
                                                        />
                                                        {type}
                                                    </ListSubheader>,
                                                    ...subtypes.map((subtype) => (
                                                        <MenuItem
                                                            key={subtype}
                                                            value={subtype}
                                                            className="filter-menu-subitem"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                const protein = subtype;
                                                                if (filters.multiSelect.excludedProteins.includes(protein)) {
                                                                    setFilter('multiSelect', 'excludedProteins',
                                                                        filters.multiSelect.excludedProteins.filter(p => p !== protein)
                                                                    );
                                                                } else {
                                                                    setFilter('multiSelect', 'excludedProteins', [...filters.multiSelect.excludedProteins, protein]);
                                                                }
                                                            }}
                                                        >
                                                            <Checkbox
                                                                checked={filters.multiSelect.excludedProteins.includes(subtype)}
                                                                className="filter-checkbox"
                                                            />
                                                            {subtype}
                                                        </MenuItem>
                                                    ))
                                                ])}
                                            </Select>
                                        </FormControl>
                                    </Grid>

                                    {/* Ingredients to Include Filter */}
                                    <Grid item xs={12} sm={6} md={2}>
                                        <Typography variant="subtitle2" sx={{ fontSize: '0.85rem' }}>Ingredients to Include</Typography>
                                        <FormControl size="small" className="filter-select-container" fullWidth>
                                            <Autocomplete
                                                multiple
                                                fullWidth
                                                options={NORMALIZED_INGREDIENT_OPTIONS}
                                                getOptionLabel={(option) => option.name}
                                                value={filters.multiSelect.includedIngredients}
                                                onChange={(event, newValue) => setFilter('multiSelect', 'includedIngredients', newValue)}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        placeholder="Search ingredients to include..."
                                                        size="small"
                                                        className="filter-select"
                                                        sx={{
                                                            backgroundColor: 'white'
                                                        }}
                                                    />
                                                )}
                                                renderTags={(value, getTagProps) =>
                                                    value.map((option, index) => (
                                                        <Chip
                                                            label={option.name}
                                                            size="small"
                                                            {...getTagProps({ index })}
                                                        />
                                                    ))
                                                }
                                                renderOption={(props, option, { selected }) => (
                                                    <MenuItem {...props} className="filter-menu-item">
                                                        <Checkbox checked={selected} className="filter-checkbox" />
                                                        <ListItemText primary={option.name} />
                                                    </MenuItem>
                                                )}
                                                size="small"
                                                disablePortal
                                                disableCloseOnSelect
                                                noOptionsText="No ingredients found"
                                                ListboxProps={{
                                                    style: { maxHeight: '300px' }
                                                }}
                                                PopperProps={{
                                                    style: { width: 'fit-content' }
                                                }}
                                                filterOptions={(options, { inputValue }) => {
                                                    const filtered = options.filter(option =>
                                                        option.name.toLowerCase().includes(inputValue.toLowerCase())
                                                    );
                                                    return filtered;
                                                }}
                                            />
                                        </FormControl>
                                    </Grid>
                                </Grid>

                                {/* Row 3: Boolean Filters */}
                                <Grid container spacing={2} sx={{ width: '100%' }}>
                                    <Grid item xs={3} md={2}>
                                        <Typography variant="subtitle2" sx={{ fontSize: '0.85rem' }}>Grain-Free</Typography>
                                        <MultiSelect
                                            label="Grain-Free"
                                            options={filters.boolean.grainFree}
                                            value={filters.boolean.grainFree}
                                            onChange={(value) => setFilter('boolean', 'grainFree', value)}
                                            renderValue={() => renderSelectedItems(filters.boolean.grainFree, {
                                                TRUE: CONSTANTS.BOOLEAN_OPTIONS.GRAIN_FREE.TRUE,
                                                FALSE: CONSTANTS.BOOLEAN_OPTIONS.GRAIN_FREE.FALSE
                                            })}
                                            itemMap={{
                                                TRUE: CONSTANTS.BOOLEAN_OPTIONS.GRAIN_FREE.TRUE,
                                                FALSE: CONSTANTS.BOOLEAN_OPTIONS.GRAIN_FREE.FALSE
                                            }}
                                                 backgroundColor="white"
                                        />
                                    </Grid>

                                    <Grid item xs={3} md={2}>
                                        <Typography variant="subtitle2" sx={{ fontSize: '0.85rem' }}>Raw Food</Typography>
                                        <MultiSelect
                                            label="Raw Food"
                                            options={filters.boolean.rawFood}
                                            value={filters.boolean.rawFood}
                                            onChange={(value) => setFilter('boolean', 'rawFood', value)}
                                            renderValue={() => renderSelectedItems(filters.boolean.rawFood, {
                                                TRUE: CONSTANTS.BOOLEAN_OPTIONS.RAW_FOOD.TRUE,
                                                FALSE: CONSTANTS.BOOLEAN_OPTIONS.RAW_FOOD.FALSE
                                            })}
                                            itemMap={{
                                                TRUE: CONSTANTS.BOOLEAN_OPTIONS.RAW_FOOD.TRUE,
                                                FALSE: CONSTANTS.BOOLEAN_OPTIONS.RAW_FOOD.FALSE
                                            }}
                                                 backgroundColor="white"
                                        />
                                    </Grid>

                                    <Grid item xs={3} md={2}>
                                        <Typography variant="subtitle2" sx={{ fontSize: '0.85rem' }}>Novelty Proteins</Typography>
                                        <MultiSelect
                                            options={filters.boolean.noveltyProteins || []}
                                            value={filters.boolean.noveltyProteins || []}
                                            onChange={(value) => {
                                                // Make options mutually exclusive
                                                const currentValue = filters.boolean.noveltyProteins || [];
                                                const newValue = Array.isArray(value) ? value : [];
                                                
                                                // If both options are selected, keep only the one that was just clicked
                                                if (newValue.includes("TRUE") && newValue.includes("NOVEL_ONLY")) {
                                                    // Find which option was just added (wasn't in current value)
                                                    const justAdded = newValue.find(v => !currentValue.includes(v));
                                                    if (justAdded) {
                                                        // Keep only the newly selected option
                                                        setFilter('boolean', 'noveltyProteins', [justAdded]);
                                                    } else {
                                                        // Both were already selected, keep only the one that wasn't removed
                                                        // (This handles the case where user clicks to deselect one)
                                                        setFilter('boolean', 'noveltyProteins', newValue.filter(v => v === "TRUE" || v === "NOVEL_ONLY"));
                                                    }
                                                } else {
                                                    // Normal case: only one or none selected
                                                    setFilter('boolean', 'noveltyProteins', newValue);
                                                }
                                            }}
                                            renderValue={() => renderSelectedItems(filters.boolean.noveltyProteins || [], {
                                                TRUE: CONSTANTS.BOOLEAN_OPTIONS.NOVELTY_PROTEINS.TRUE,
                                                NOVEL_ONLY: CONSTANTS.BOOLEAN_OPTIONS.NOVELTY_PROTEINS.NOVEL_ONLY
                                            })}
                                            itemMap={{
                                                TRUE: CONSTANTS.BOOLEAN_OPTIONS.NOVELTY_PROTEINS.TRUE,
                                                NOVEL_ONLY: CONSTANTS.BOOLEAN_OPTIONS.NOVELTY_PROTEINS.NOVEL_ONLY
                                            }}
                                            backgroundColor="white"
                                        />
                                    </Grid>

                                    <Grid item xs={3} md={2}>
                                        <Typography variant="subtitle2" sx={{ fontSize: '0.85rem' }}>Prescription</Typography>
                                        <MultiSelect
                                            label="Prescription"
                                            options={filters.boolean.prescription}
                                            value={filters.boolean.prescription}
                                            onChange={(value) => setFilter('boolean', 'prescription', value)}
                                            renderValue={() => renderSelectedItems(filters.boolean.prescription, {
                                                TRUE: CONSTANTS.BOOLEAN_OPTIONS.PRESCRIPTION.TRUE,
                                                FALSE: CONSTANTS.BOOLEAN_OPTIONS.PRESCRIPTION.FALSE
                                            })}
                                            itemMap={{
                                                TRUE: CONSTANTS.BOOLEAN_OPTIONS.PRESCRIPTION.TRUE,
                                                FALSE: CONSTANTS.BOOLEAN_OPTIONS.PRESCRIPTION.FALSE
                                            }}
                                                 backgroundColor="white"
                                        />
                                    </Grid>

                                </Grid>
                                <Grid container spacing={2} sx={{ width: '100%', mt: 2 }}>

                                    <Grid item xs={3} md={2}>
                                        <Typography variant="subtitle2" sx={{ fontSize: '0.85rem' }}>Limited Ingredient Diet</Typography>
                                        <Box sx={booleanCheckboxWrapperSx} className="filter-select-container">
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={(filters.boolean.lid || []).includes('TRUE')}
                                                        onChange={(event) =>
                                                            setFilter(
                                                                'boolean',
                                                                'lid',
                                                                event.target.checked ? ['TRUE'] : []
                                                            )
                                                        }
                                                        size="small"
                                                        sx={whiteCheckboxSx}
                                                    />
                                                }
                                                label="Limited Ingredient Diet only"
                                                sx={booleanCheckboxLabelSx}
                                            />
                                        </Box>
                                    </Grid>

                                    <Grid item xs={3} md={2}>
                                        <Typography variant="subtitle2" sx={{ fontSize: '0.85rem' }}>Single Protein</Typography>
                                        <Box sx={booleanCheckboxWrapperSx} className="filter-select-container">
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={(filters.boolean.singleProtein || []).includes('TRUE')}
                                                        onChange={(event) =>
                                                            setFilter(
                                                                'boolean',
                                                                'singleProtein',
                                                                event.target.checked ? ['TRUE'] : []
                                                            )
                                                        }
                                                        size="small"
                                                        sx={whiteCheckboxSx}
                                                    />
                                                }
                                                label="Single Protein only"
                                                sx={booleanCheckboxLabelSx}
                                            />
                                        </Box>
                                    </Grid>

                                    <Grid item xs={3} md={2}>
                                        <Typography variant="subtitle2" sx={{ fontSize: '0.85rem' }}>Hydrolyzed Protein</Typography>
                                        <Box sx={booleanCheckboxWrapperSx} className="filter-select-container">
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={(filters.boolean.hydrolyzedProtein || []).includes('TRUE')}
                                                        onChange={(event) =>
                                                            setFilter(
                                                                'boolean',
                                                                'hydrolyzedProtein',
                                                                event.target.checked ? ['TRUE'] : []
                                                            )
                                                        }
                                                        size="small"
                                                        sx={whiteCheckboxSx}
                                                    />
                                                }
                                                label="Hydrolyzed Proteins only"
                                                sx={booleanCheckboxLabelSx}
                                            />
                                        </Box>
                                    </Grid>

                                </Grid>
                        </Box>
                    </Grid>
                 ) : (
                     // Desktop: Show collapsible panel
                     <Grid item xs={12} sx={{ 
                         width: '100%',
                         display: 'flex',
                         justifyContent: 'center',
                         mt: open ? 1.5 : 1,
                         mb: open ? 2 : 1
                     }}>
                                         <Paper 
                         elevation={2} 
                         sx={{ 
                             px: 3,
                             py: open ? 3 : 1.25,
                             width: '100%',
                             border: hasActiveDetailedFilters() ? '2px solid #1976d2' : '1px solid #e0e0e0',
                             borderRadius: 2,
                             backgroundColor: hasActiveDetailedFilters() ? '#f3f8ff' : '#fafafa',
                             transition: 'padding 0.4s ease-in-out'
                         }}
                     >
                                                 {/* Header with enhanced visibility */}
                         <Box sx={{ 
                             display: 'flex', 
                             alignItems: 'center', 
                             justifyContent: 'space-between',
                             mb: open ? 1 : 0,
                             gap: 1.5
                         }}>
                                                         <Box sx={{ 
                                 display: 'flex', 
                                 alignItems: 'center', 
                                 gap: 1,
                                 flex: 1
                             }}>
                                 <Typography variant="h6" sx={{ 
                                     fontWeight: 600,
                                     color: hasActiveDetailedFilters() ? '#1976d2' : '#333',
                                     fontSize: open ? '1.25rem' : '1rem',
                                     lineHeight: 1.3
                                 }}>
                                     Advanced Filters
                                 </Typography>
                                 {!open && (
                                     <Typography 
                                         variant="body2" 
                                         sx={{ 
                                             color: 'text.secondary',
                                             fontSize: '0.85rem',
                                             ml: 1
                                         }}
                                     >
                                         Filter on nutrient values and product attributes
                                     </Typography>
                                 )}
                                 {hasActiveDetailedFilters() && !open && (
                                     <Chip 
                                         label={`${getActiveDetailedFiltersCount()} active`}
                                         size="small"
                                         color="primary"
                                         sx={{ 
                                             height: '22px',
                                             fontSize: '0.75rem',
                                             fontWeight: 500,
                                             ml: 1,
                                             '& .MuiChip-label': {
                                                 px: 1
                                             }
                                         }}
                                     />
                                 )}
                                 {hasActiveDetailedFilters() && open && (
                                     <Chip 
                                         label={`${getActiveDetailedFiltersCount()} active`}
                                         size="small"
                                         color="primary"
                                         sx={{ 
                                             ml: 1,
                                             fontSize: '0.75rem',
                                             height: '24px'
                                         }}
                                     />
                                 )}
                             </Box>
                            <Button
                                variant="contained"
                                disableRipple
                                onClick={() => setOpen(!open)}
                                startIcon={
                                    open ? (
                                        <ExpandLessIcon />
                                    ) : (
                                        <FilterListIcon sx={{ fontSize: '1.2rem' }} />
                                    )
                                }
                                sx={{
                                    backgroundColor: '#42a5f5 !important',
                                    color: '#fff',
                                    border: 'none',
                                    textTransform: 'none',
                                    fontWeight: 700,
                                    fontSize: '0.95rem',
                                    padding: '6px 14px',
                                    width: '170px',
                                    minWidth: '170px',
                                    maxWidth: '170px',
                                    borderRadius: '12px',
                                    boxShadow: 'none !important',
                                    position: 'relative',
                                    '&:hover': {
                                        backgroundColor: '#1976d2 !important'
                                    },
                                    '&:focus': {
                                        boxShadow: 'none !important',
                                        outline: 'none',
                                        backgroundColor: '#42a5f5 !important'
                                    },
                                    '&:focus-visible': {
                                        boxShadow: 'none !important',
                                        outline: 'none',
                                        backgroundColor: '#42a5f5 !important'
                                    },
                                    '&:active': {
                                        boxShadow: 'none !important',
                                        backgroundColor: '#42a5f5 !important'
                                    },
                                    '&.Mui-focusVisible': {
                                        backgroundColor: '#42a5f5 !important',
                                        boxShadow: 'none !important'
                                    },
                                    '& .MuiButton-startIcon': {
                                        width: '20px',
                                        display: 'inline-flex',
                                        justifyContent: 'center',
                                        marginRight: '10px',
                                        marginLeft: 0
                                    }
                                }}
                            >
                                {open ? 'Hide Filters' : 'Show Filters'}
                            </Button>
                        </Box>

                {/* Collapsed Filters */}
                    <Collapse 
                        in={open}
                        timeout={400}
                        easing={{ enter: 'ease-in-out', exit: 'ease-in-out' }}
                    >
                             <Divider sx={{ my: 1 }} />
                             <Box className="detailed-filters-container" sx={{ width: '100%', mt: 1, pt: 1 }}>
                                {/* Row 1: Nutrient Filters */}
                                <Grid container spacing={2} sx={{ width: '100%', mb: 2 }}>
                                    {/* Protein % Filter */}
                                    <Grid item xs={12} sm={6} md={4}>
                                        <NumberRangeFilter
                                            label="Protein (%)"
                                            minValue={filters.range.protein.min}
                                            maxValue={filters.range.protein.max}
                                            onMinChange={(value) => setFilter('range', 'protein', { min: value })}
                                            onMaxChange={(value) => setFilter('range', 'protein', { max: value })}
                                        />
                                    </Grid>

                                    {/* Phosphorus Filter */}
                                    <Grid item xs={3} md={2}>
                                        <Typography variant="subtitle2" sx={{ fontSize: '0.85rem' }}>Phosphorus (%)</Typography>
                                        <input
                                            type="number"
                                            value={filters.range.phosphorus.max}
                                            onChange={(e) => setFilter('range', 'phosphorus', { max: e.target.value })}
                                            placeholder="Max"
                                            className="single-number-input"
                                        />
                                    </Grid>

                                    {/* Sodium Filter */}
                                    <Grid item xs={3} md={2}>
                                        <Typography variant="subtitle2" sx={{ fontSize: '0.85rem' }}>Sodium (%)</Typography>
                                        <input
                                            type="number"
                                            value={filters.range.sodium.max}
                                            onChange={(e) => setFilter('range', 'sodium', { max: e.target.value })}
                                            placeholder="Max"
                                            className="single-number-input"
                                        />
                                    </Grid>

                                    {/* Carbs Filter */}
                                    <Grid item xs={12} sm={6} md={4}>
                                        <NumberRangeFilter
                                            label="Carbs (%)"
                                            minValue={filters.range.carbs.min}
                                            maxValue={filters.range.carbs.max}
                                            onMinChange={(value) => setFilter('range', 'carbs', { min: value })}
                                            onMaxChange={(value) => setFilter('range', 'carbs', { max: value })}
                                        />
                                    </Grid>

                                    {/* Calcium Filter */}
                                    <Grid item xs={3} md={2}>
                                        <Typography variant="subtitle2" sx={{ fontSize: '0.85rem' }}>Calcium (%)</Typography>
                                        <input
                                            type="number"
                                            value={filters.range.calcium.max}
                                            onChange={(e) => setFilter('range', 'calcium', { max: e.target.value })}
                                            placeholder="Max"
                                            className="single-number-input"
                                        />
                                    </Grid>

                                    {/* Fat Filter */}
                                    <Grid item xs={3} md={2}>
                                        <Typography variant="subtitle2" sx={{ fontSize: '0.85rem' }}>Fat (%)</Typography>
                                        <input
                                            type="number"
                                            value={filters.range.fat.max}
                                            onChange={(e) => setFilter('range', 'fat', { max: e.target.value })}
                                            placeholder="Max"
                                            className="single-number-input"
                                        />
                                    </Grid>

                                    <Grid item xs={3} md={2}>
                                        <Typography variant="subtitle2" sx={{ fontSize: '0.85rem' }}>Guaranteed Analysis (Less accurate)</Typography>
                                        <Box sx={booleanCheckboxWrapperSx} className="filter-select-container">
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={(filters.boolean.includeGuaranteedAnalysis || []).includes('TRUE')}
                                                        onChange={(event) =>
                                                            setFilter(
                                                                'boolean',
                                                                'includeGuaranteedAnalysis',
                                                                event.target.checked ? ['TRUE'] : []
                                                            )
                                                        }
                                                        size="small"
                                                        sx={whiteCheckboxSx}
                                                    />
                                                }
                                                label="Include Guaranteed Analysis"
                                                sx={booleanCheckboxLabelSx}
                                            />
                                        </Box>
                                    </Grid>
                                </Grid>

                                {/* Calories Filter Section */}
                                <Grid container spacing={3} sx={{ width: '100%', mb: 2 }}>
                                    <Grid item xs={12}>
                                        <NumberRangeFilter
                                            label="Calories (kcal per kg)"
                                            minValue={filters.range.calories.min}
                                            maxValue={filters.range.calories.max}
                                            onMinChange={(value) => setFilter('range', 'calories', { min: value })}
                                            onMaxChange={(value) => setFilter('range', 'calories', { max: value })}
                                        />
                                    </Grid>
                                </Grid>

                                {/* Row 2: Proteins to Exclude and Ingredients to Include Filters */}
                                <Grid container spacing={2} sx={{ width: '100%', mb: 2 }}>
                                    <Grid item xs={12} sm={6} md={2}>
                                        <Typography variant="subtitle2" sx={{ fontSize: '0.85rem' }}>Proteins to Exclude</Typography>
                                        <FormControl size="small" className="filter-select-container" fullWidth>
                                            <Select
                                                multiple
                                                value={filters.multiSelect.excludedProteins}
                                                onChange={(event) => {
                                                    const protein = event.target.value;
                                                    if (typeof protein === 'string') {
                                                        if (filters.multiSelect.excludedProteins.includes(protein)) {
                                                            setFilter('multiSelect', 'excludedProteins',
                                                                filters.multiSelect.excludedProteins.filter(p => p !== protein)
                                                            );
                                                        } else {
                                                            setFilter('multiSelect', 'excludedProteins', [...filters.multiSelect.excludedProteins, protein]);
                                                        }
                                                    } else if (Array.isArray(protein)) {
                                                        setFilter('multiSelect', 'excludedProteins', protein);
                                                    }
                                                }}
                                                renderValue={(selected) => {
                                                    if (!Array.isArray(selected) || selected.length === 0) {
                                                        return "None selected";
                                                    }
                                                    const validSelections = selected.filter(item => item && item.trim() !== '');
                                                    if (validSelections.length === 0) {
                                                        return "None selected";
                                                    }
                                                    return validSelections.reduce((acc, curr) => acc + ', ' + curr);
                                                }}
                                                MenuProps={{
                                                    PaperProps: {
                                                        style: { maxHeight: 400, minWidth: 300 },
                                                    },
                                                    disableScrollLock: true,
                                                }}
                                                className="filter-select"
                                                displayEmpty
                                                sx={{
                                                    backgroundColor: 'white'
                                                }}
                                            >
                                                {Object.entries(enums.proteinTypes).flatMap(([type, subtypes]) => [
                                                    <ListSubheader key={`header-excluded-${type}`} className="filter-subheader">
                                                        <Checkbox
                                                            checked={subtypes.every(subtype => filters.multiSelect.excludedProteins.includes(subtype))}
                                                            indeterminate={
                                                                subtypes.some(subtype => filters.multiSelect.excludedProteins.includes(subtype)) &&
                                                                !subtypes.every(subtype => filters.multiSelect.excludedProteins.includes(subtype))
                                                            }
                                                            onChange={(e) => {
                                                                const selectedType = type;
                                                                const typesProteins = enums.proteinTypes[selectedType];
                                                                const allTypeProteinsSelected = typesProteins.every(protein =>
                                                                    filters.multiSelect.excludedProteins.includes(protein)
                                                                );
                                                                if (allTypeProteinsSelected) {
                                                                    setFilter('multiSelect', 'excludedProteins',
                                                                        filters.multiSelect.excludedProteins.filter(protein => !typesProteins.includes(protein))
                                                                    );
                                                                } else {
                                                                    const currentProteins = new Set(filters.multiSelect.excludedProteins);
                                                                    typesProteins.forEach(protein => currentProteins.add(protein));
                                                                    setFilter('multiSelect', 'excludedProteins', Array.from(currentProteins));
                                                                }
                                                            }}
                                                            value={type}
                                                            onClick={(e) => e.stopPropagation()}
                                                        />
                                                        {type}
                                                    </ListSubheader>,
                                                    ...subtypes.map((subtype) => (
                                                        <MenuItem
                                                            key={subtype}
                                                            value={subtype}
                                                            className="filter-menu-subitem"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                const protein = subtype;
                                                                if (filters.multiSelect.excludedProteins.includes(protein)) {
                                                                    setFilter('multiSelect', 'excludedProteins',
                                                                        filters.multiSelect.excludedProteins.filter(p => p !== protein)
                                                                    );
                                                                } else {
                                                                    setFilter('multiSelect', 'excludedProteins', [...filters.multiSelect.excludedProteins, protein]);
                                                                }
                                                            }}
                                                        >
                                                            <Checkbox
                                                                checked={filters.multiSelect.excludedProteins.includes(subtype)}
                                                                className="filter-checkbox"
                                                            />
                                                            {subtype}
                                                        </MenuItem>
                                                    ))
                                                ])}
                                            </Select>
                                        </FormControl>
                                    </Grid>

                                    {/* Ingredients to Include Filter */}
                                    <Grid item xs={12} sm={6} md={2}>
                                        <Typography variant="subtitle2" sx={{ fontSize: '0.85rem' }}>Ingredients to Include</Typography>
                                        <FormControl size="small" className="filter-select-container" fullWidth>
                                            <Autocomplete
                                                multiple
                                                fullWidth
                                                options={NORMALIZED_INGREDIENT_OPTIONS}
                                                getOptionLabel={(option) => option.name}
                                                value={filters.multiSelect.includedIngredients}
                                                onChange={(event, newValue) => setFilter('multiSelect', 'includedIngredients', newValue)}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        placeholder="Search ingredients to include..."
                                                        size="small"
                                                        className="filter-select"
                                                        sx={{
                                                            backgroundColor: 'white'
                                                        }}
                                                    />
                                                )}
                                                renderTags={(value, getTagProps) =>
                                                    value.map((option, index) => (
                                                        <Chip
                                                            label={option.name}
                                                            size="small"
                                                            {...getTagProps({ index })}
                                                        />
                                                    ))
                                                }
                                                renderOption={(props, option, { selected }) => (
                                                    <MenuItem {...props} className="filter-menu-item">
                                                        <Checkbox checked={selected} className="filter-checkbox" />
                                                        <ListItemText primary={option.name} />
                                                    </MenuItem>
                                                )}
                                                size="small"
                                                disablePortal
                                                disableCloseOnSelect
                                                noOptionsText="No ingredients found"
                                                ListboxProps={{
                                                    style: { maxHeight: '300px' }
                                                }}
                                                PopperProps={{
                                                    style: { width: 'fit-content' }
                                                }}
                                                filterOptions={(options, { inputValue }) => {
                                                    const filtered = options.filter(option =>
                                                        option.name.toLowerCase().includes(inputValue.toLowerCase())
                                                    );
                                                    return filtered;
                                                }}
                                            />
                                        </FormControl>
                                    </Grid>
                                </Grid>

                                {/* Row 3: Boolean Filters */}
                                <Grid container spacing={2} sx={{ width: '100%' }}>
                                    <Grid item xs={3} md={2}>
                                        <Typography variant="subtitle2" sx={{ fontSize: '0.85rem' }}>Grain-Free</Typography>
                                        <MultiSelect
                                            label="Grain-Free"
                                            options={filters.boolean.grainFree}
                                            value={filters.boolean.grainFree}
                                            onChange={(value) => setFilter('boolean', 'grainFree', value)}
                                            renderValue={() => renderSelectedItems(filters.boolean.grainFree, {
                                                TRUE: CONSTANTS.BOOLEAN_OPTIONS.GRAIN_FREE.TRUE,
                                                FALSE: CONSTANTS.BOOLEAN_OPTIONS.GRAIN_FREE.FALSE
                                            })}
                                            itemMap={{
                                                TRUE: CONSTANTS.BOOLEAN_OPTIONS.GRAIN_FREE.TRUE,
                                                FALSE: CONSTANTS.BOOLEAN_OPTIONS.GRAIN_FREE.FALSE
                                            }}
                                                 backgroundColor="white"
                                        />
                                    </Grid>

                                    <Grid item xs={3} md={2}>
                                        <Typography variant="subtitle2" sx={{ fontSize: '0.85rem' }}>Raw Food</Typography>
                                        <MultiSelect
                                            label="Raw Food"
                                            options={filters.boolean.rawFood}
                                            value={filters.boolean.rawFood}
                                            onChange={(value) => setFilter('boolean', 'rawFood', value)}
                                            renderValue={() => renderSelectedItems(filters.boolean.rawFood, {
                                                TRUE: CONSTANTS.BOOLEAN_OPTIONS.RAW_FOOD.TRUE,
                                                FALSE: CONSTANTS.BOOLEAN_OPTIONS.RAW_FOOD.FALSE
                                            })}
                                            itemMap={{
                                                TRUE: CONSTANTS.BOOLEAN_OPTIONS.RAW_FOOD.TRUE,
                                                FALSE: CONSTANTS.BOOLEAN_OPTIONS.RAW_FOOD.FALSE
                                            }}
                                                 backgroundColor="white"
                                        />
                                    </Grid>

                                    <Grid item xs={3} md={2}>
                                        <Typography variant="subtitle2" sx={{ fontSize: '0.85rem' }}>Novelty Proteins</Typography>
                                        <MultiSelect
                                            options={filters.boolean.noveltyProteins || []}
                                            value={filters.boolean.noveltyProteins || []}
                                            onChange={(value) => {
                                                // Make options mutually exclusive
                                                const currentValue = filters.boolean.noveltyProteins || [];
                                                const newValue = Array.isArray(value) ? value : [];
                                                
                                                // If both options are selected, keep only the one that was just clicked
                                                if (newValue.includes("TRUE") && newValue.includes("NOVEL_ONLY")) {
                                                    // Find which option was just added (wasn't in current value)
                                                    const justAdded = newValue.find(v => !currentValue.includes(v));
                                                    if (justAdded) {
                                                        // Keep only the newly selected option
                                                        setFilter('boolean', 'noveltyProteins', [justAdded]);
                                                    } else {
                                                        // Both were already selected, keep only the one that wasn't removed
                                                        // (This handles the case where user clicks to deselect one)
                                                        setFilter('boolean', 'noveltyProteins', newValue.filter(v => v === "TRUE" || v === "NOVEL_ONLY"));
                                                    }
                                                } else {
                                                    // Normal case: only one or none selected
                                                    setFilter('boolean', 'noveltyProteins', newValue);
                                                }
                                            }}
                                            renderValue={() => renderSelectedItems(filters.boolean.noveltyProteins || [], {
                                                TRUE: CONSTANTS.BOOLEAN_OPTIONS.NOVELTY_PROTEINS.TRUE,
                                                NOVEL_ONLY: CONSTANTS.BOOLEAN_OPTIONS.NOVELTY_PROTEINS.NOVEL_ONLY
                                            })}
                                            itemMap={{
                                                TRUE: CONSTANTS.BOOLEAN_OPTIONS.NOVELTY_PROTEINS.TRUE,
                                                NOVEL_ONLY: CONSTANTS.BOOLEAN_OPTIONS.NOVELTY_PROTEINS.NOVEL_ONLY
                                            }}
                                            backgroundColor="white"
                                        />
                                    </Grid>

                                    <Grid item xs={3} md={2}>
                                        <Typography variant="subtitle2" sx={{ fontSize: '0.85rem' }}>Prescription</Typography>
                                        <MultiSelect
                                            label="Prescription"
                                            options={filters.boolean.prescription}
                                            value={filters.boolean.prescription}
                                            onChange={(value) => setFilter('boolean', 'prescription', value)}
                                            renderValue={() => renderSelectedItems(filters.boolean.prescription, {
                                                TRUE: CONSTANTS.BOOLEAN_OPTIONS.PRESCRIPTION.TRUE,
                                                FALSE: CONSTANTS.BOOLEAN_OPTIONS.PRESCRIPTION.FALSE
                                            })}
                                            itemMap={{
                                                TRUE: CONSTANTS.BOOLEAN_OPTIONS.PRESCRIPTION.TRUE,
                                                FALSE: CONSTANTS.BOOLEAN_OPTIONS.PRESCRIPTION.FALSE
                                            }}
                                                 backgroundColor="white"
                                        />
                                    </Grid>

                                </Grid>
                                <Grid container spacing={2} sx={{ width: '100%', mt: 2 }}>

                                    <Grid item xs={3} md={2}>
                                        <Typography variant="subtitle2" sx={{ fontSize: '0.85rem' }}>Limited Ingredient Diet</Typography>
                                        <Box sx={booleanCheckboxWrapperSx} className="filter-select-container">
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={(filters.boolean.lid || []).includes('TRUE')}
                                                        onChange={(event) =>
                                                            setFilter(
                                                                'boolean',
                                                                'lid',
                                                                event.target.checked ? ['TRUE'] : []
                                                            )
                                                        }
                                                        size="small"
                                                        sx={whiteCheckboxSx}
                                                    />
                                                }
                                                label="Limited Ingredient Diet only"
                                                sx={booleanCheckboxLabelSx}
                                            />
                                        </Box>
                                    </Grid>

                                    <Grid item xs={3} md={2}>
                                        <Typography variant="subtitle2" sx={{ fontSize: '0.85rem' }}>Single Protein</Typography>
                                        <Box sx={booleanCheckboxWrapperSx} className="filter-select-container">
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={(filters.boolean.singleProtein || []).includes('TRUE')}
                                                        onChange={(event) =>
                                                            setFilter(
                                                                'boolean',
                                                                'singleProtein',
                                                                event.target.checked ? ['TRUE'] : []
                                                            )
                                                        }
                                                        size="small"
                                                        sx={whiteCheckboxSx}
                                                    />
                                                }
                                                label="Single Protein only"
                                                sx={booleanCheckboxLabelSx}
                                            />
                                        </Box>
                                    </Grid>

                                    <Grid item xs={3} md={2}>
                                        <Typography variant="subtitle2" sx={{ fontSize: '0.85rem' }}>Hydrolyzed Protein</Typography>
                                        <Box sx={booleanCheckboxWrapperSx} className="filter-select-container">
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={(filters.boolean.hydrolyzedProtein || []).includes('TRUE')}
                                                        onChange={(event) =>
                                                            setFilter(
                                                                'boolean',
                                                                'hydrolyzedProtein',
                                                                event.target.checked ? ['TRUE'] : []
                                                            )
                                                        }
                                                        size="small"
                                                        sx={whiteCheckboxSx}
                                                    />
                                                }
                                                label="Hydrolyzed Proteins only"
                                                sx={booleanCheckboxLabelSx}
                                            />
                                        </Box>
                                    </Grid>

                                </Grid>
                        </Box>
                    </Collapse>
                    </Paper>
                </Grid>
                 )}

                                 {/* Action Buttons */}
                 {hasActiveFilters() && (
                     <Grid item xs={12} sx={{ 
                         width: '100%',
                         display: 'flex',
                         justifyContent: 'center',
                         mt: 0,
                         mb: 2.5,
                         // Mobile-specific centering
                         '@media (max-width: 768px)': {
                             justifyContent: 'center',
                             mt: 0,
                             mb: 2.5
                         }
                     }}>
                        <Button
                            variant="outlined"
                            disableRipple
                            onClick={handleResetFilters}
                            className="clear-filters-button"
                            sx={{ 
                                minWidth: '200px',
                                maxWidth: '300px',
                                flex: 'none',
                                fontWeight: 600,
                                boxShadow: 'none !important',
                                '&:focus': {
                                    boxShadow: 'none !important',
                                    outline: 'none',
                                    backgroundColor: 'transparent !important',
                                    borderColor: '#42a5f5 !important'
                                },
                                '&:focus-visible': {
                                    boxShadow: 'none !important',
                                    outline: 'none',
                                    backgroundColor: 'transparent !important',
                                    borderColor: '#42a5f5 !important'
                                },
                                '&:active': {
                                    boxShadow: 'none !important',
                                    backgroundColor: 'transparent !important',
                                    borderColor: '#42a5f5 !important'
                                },
                                '&.Mui-focusVisible': {
                                    backgroundColor: 'transparent !important',
                                    boxShadow: 'none !important',
                                    borderColor: '#42a5f5 !important'
                                }
                            }}
                        >
                            Clear All Filters
                        </Button>
                     </Grid>
                 )}
            </Grid>
        </Box>
    );
};

export default SearchFilterPanel;